import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Shield, Save, Trash2, Eye, EyeOff, ExternalLink, Info, CheckCircle, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GoogleOAuthSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [settings, setSettings] = useState({
    client_id: '',
    client_secret: '',
    enabled: false
  });
  const [currentSettings, setCurrentSettings] = useState({
    configured: false,
    client_id: '',
    client_secret_masked: '',
    enabled: false
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/oauth-settings/platform/google`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentSettings(response.data);
      setSettings({
        client_id: response.data.client_id || '',
        client_secret: '', // Never pre-fill secret
        enabled: response.data.enabled || false
      });
    } catch (error) {
      console.error('Failed to load OAuth settings:', error);
      toast.error('Failed to load Google OAuth settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings.client_id) {
      toast.error('Please enter a Client ID');
      return;
    }
    
    // If updating existing settings, secret is optional (keep existing)
    if (!currentSettings.configured && !settings.client_secret) {
      toast.error('Please enter a Client Secret');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const payload = {
        client_id: settings.client_id,
        client_secret: settings.client_secret || 'KEEP_EXISTING', // Backend should handle this
        enabled: settings.enabled
      };

      await axios.post(`${API_URL}/api/oauth-settings/platform/google`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Google OAuth settings saved successfully');
      loadSettings(); // Refresh to show updated data
      setSettings(prev => ({ ...prev, client_secret: '' })); // Clear secret field
    } catch (error) {
      console.error('Failed to save OAuth settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the Google OAuth configuration? Users will no longer be able to sign in with Google.')) {
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/oauth-settings/platform/google`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Google OAuth settings deleted');
      loadSettings();
    } catch (error) {
      console.error('Failed to delete OAuth settings:', error);
      toast.error('Failed to delete settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="h-6 w-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </div>
            <div>
              <CardTitle>Google OAuth Configuration</CardTitle>
              <CardDescription>Platform-level Google Sign-In settings</CardDescription>
            </div>
          </div>
          {currentSettings.configured ? (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <CheckCircle className="h-3 w-3 mr-1" />
              Configured
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
              <AlertCircle className="h-3 w-3 mr-1" />
              Not Configured
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Info Alert */}
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Configure your Google Cloud OAuth credentials to enable "Sign in with Google" for all users. 
            Clinics can optionally override these settings with their own credentials.
            <a 
              href="https://console.cloud.google.com/apis/credentials" 
              target="_blank" 
              rel="noopener noreferrer"
              className="ml-2 text-teal-600 hover:underline inline-flex items-center"
            >
              Google Cloud Console <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          </AlertDescription>
        </Alert>

        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <Label className="text-base font-medium">Enable Google Sign-In</Label>
            <p className="text-sm text-gray-500">Allow users to sign in with their Google account</p>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
          />
        </div>

        {/* Client ID */}
        <div className="space-y-2">
          <Label htmlFor="client_id">Client ID</Label>
          <Input
            id="client_id"
            type="text"
            placeholder="Your Google OAuth Client ID"
            value={settings.client_id}
            onChange={(e) => setSettings(prev => ({ ...prev, client_id: e.target.value }))}
          />
          {currentSettings.configured && currentSettings.client_id && (
            <p className="text-xs text-gray-500">
              Current: {currentSettings.client_id.substring(0, 30)}...
            </p>
          )}
        </div>

        {/* Client Secret */}
        <div className="space-y-2">
          <Label htmlFor="client_secret">Client Secret</Label>
          <div className="relative">
            <Input
              id="client_secret"
              type={showSecret ? 'text' : 'password'}
              placeholder={currentSettings.configured ? 'Leave blank to keep existing secret' : 'Your Google OAuth Client Secret'}
              value={settings.client_secret}
              onChange={(e) => setSettings(prev => ({ ...prev, client_secret: e.target.value }))}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowSecret(!showSecret)}
            >
              {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {currentSettings.configured && currentSettings.client_secret_masked && (
            <p className="text-xs text-gray-500">
              Current secret: {currentSettings.client_secret_masked}
            </p>
          )}
        </div>

        {/* Redirect URI Info */}
        <div className="p-4 bg-gray-50 rounded-lg space-y-2">
          <Label className="text-sm font-medium">Authorized Redirect URIs</Label>
          <p className="text-xs text-gray-500">Add these URIs to your Google OAuth consent screen:</p>
          <code className="block text-xs bg-white p-2 rounded border">
            {window.location.origin}/auth/google/callback
          </code>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-teal-600 hover:bg-teal-700"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
          
          {currentSettings.configured && (
            <Button 
              variant="outline" 
              onClick={handleDelete}
              disabled={saving}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Configuration
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default GoogleOAuthSettings;
